import { Minimize2, Plus } from "lucide-react";
import { CSSProperties, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router";

import { StatusPill } from "nazare-ui/components";
import { cn } from "nazare-ui/utils";

import { useDocumentTitle } from "../../../../use-document-title";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./infra.module.css";

type ScheduleStatus = "enabled" | "disabled";

type InfraScheduleType =
  | "ECS task"
  | "Lambda function"
  | "Step Function"
  | "EventBridge rule";

type InfraSchedule = {
  id: string;
  name: string;
  type: InfraScheduleType;
  cron: string;
  status: ScheduleStatus;
  /** Hardcoded display string, not a `Date` — Workflow scripts forbid
   * non-deterministic calls like `new Date()`/`Date.now()`. */
  nextRun: string;
};

/**
 * Static mock infra schedules — there is no live infra-scheduling backend in
 * `platform` yet. Shaped after the reference `/admin/workflows` dashboard's
 * "Infra schedules" card (see `../summary-cards.tsx`'s `INFRA_SCHEDULES`),
 * but defined independently here and extended from that card's ~8 sample
 * rows to the ~30-row set implied by the reference dashboard's count badge
 * (25). Real-looking AWS scheduled-task names/crons, not placeholders.
 */
const INFRA_SCHEDULES: InfraSchedule[] = [
  {
    id: "infra-companies-house-filings-sync",
    name: "companies_house_filings_sync-ingest",
    type: "ECS task",
    cron: "cron(0 2 * * ? *)",
    status: "enabled",
    nextRun: "in 3h 41 min",
  },
  {
    id: "infra-crunchbase-delta-sync",
    name: "crunchbase_delta_sync-ingest",
    type: "ECS task",
    cron: "cron(30 3 * * ? *)",
    status: "enabled",
    nextRun: "in 4h 11 min",
  },
  {
    id: "infra-pitchbook-export-ingest",
    name: "pitchbook_export_ingest-batch",
    type: "Lambda function",
    cron: "cron(0 5 ? * MON *)",
    status: "disabled",
    nextRun: "13 Jul, 06:00",
  },
  {
    id: "infra-affinity-crm-sync",
    name: "affinity_crm_sync-ingest",
    type: "ECS task",
    cron: "cron(*/30 * * * ? *)",
    status: "enabled",
    nextRun: "in 12 min",
  },
  {
    id: "infra-docusign-envelope-archive",
    name: "docusign_envelope_archive-nightly",
    type: "Lambda function",
    cron: "cron(0 1 * * ? *)",
    status: "enabled",
    nextRun: "in 2h 46 min",
  },
  {
    id: "infra-carta-cap-table-sync",
    name: "carta_cap_table_sync-ingest",
    type: "ECS task",
    cron: "cron(0 6 * * ? *)",
    status: "enabled",
    nextRun: "in 7h 03 min",
  },
  {
    id: "infra-google-drive-permissions-audit",
    name: "google_drive_permissions_audit-nightly",
    type: "Step Function",
    cron: "cron(0 3 ? * SUN *)",
    status: "disabled",
    nextRun: "12 Jul, 04:00",
  },
  {
    id: "infra-slack-workspace-export",
    name: "slack_workspace_export-nightly",
    type: "ECS task",
    cron: "cron(15 2 * * ? *)",
    status: "enabled",
    nextRun: "in 3h 56 min",
  },
  {
    id: "infra-board-deck-index-refresh",
    name: "board_deck_index_refresh-batch",
    type: "Lambda function",
    cron: "cron(0 6 ? * MON *)",
    status: "enabled",
    nextRun: "13 Jul, 07:00",
  },
  {
    id: "infra-quarterly-valuations-export",
    name: "quarterly_valuations_export-batch",
    type: "Step Function",
    cron: "cron(0 4 1 * ? *)",
    status: "enabled",
    nextRun: "01 Aug, 05:00",
  },
  {
    id: "infra-lp-reporting-package-build",
    name: "lp_reporting_package_build-batch",
    type: "Step Function",
    cron: "cron(0 6 1 * ? *)",
    status: "disabled",
    nextRun: "01 Aug, 07:00",
  },
  {
    id: "infra-portfolio-kpi-rollup",
    name: "portfolio_kpi_rollup-nightly",
    type: "ECS task",
    cron: "cron(0 4 * * ? *)",
    status: "enabled",
    nextRun: "in 5h 08 min",
  },
  {
    id: "infra-media-mentions-scrape",
    name: "media_mentions_scrape-ingest",
    type: "ECS task",
    cron: "cron(0 */2 * * ? *)",
    status: "enabled",
    nextRun: "in 1h 22 min",
  },
  {
    id: "infra-form-d-filings-ingest",
    name: "form_d_filings_ingest-batch",
    type: "Lambda function",
    cron: "cron(0 9 * * ? *)",
    status: "disabled",
    nextRun: "08 Jul, 10:00",
  },
  {
    id: "infra-companies-house-accounts-sync",
    name: "companies_house_accounts_sync-ingest",
    type: "ECS task",
    cron: "cron(30 2 * * ? *)",
    status: "enabled",
    nextRun: "in 4h 41 min",
  },
  {
    id: "infra-esg-survey-export",
    name: "esg_survey_export-batch",
    type: "Lambda function",
    cron: "cron(0 7 ? * FRI *)",
    status: "disabled",
    nextRun: "10 Jul, 08:00",
  },
  {
    id: "infra-diligence-docs-reindex",
    name: "diligence_docs_reindex-weekly",
    type: "ECS task",
    cron: "cron(0 5 ? * SUN *)",
    status: "enabled",
    nextRun: "12 Jul, 06:00",
  },
  {
    id: "infra-employee-headcount-ingest",
    name: "employee_headcount_ingest-batch",
    type: "Lambda function",
    cron: "cron(0 8 ? * MON *)",
    status: "enabled",
    nextRun: "13 Jul, 09:00",
  },
  {
    id: "infra-vendor-risk-score-refresh",
    name: "vendor_risk_score_refresh-batch",
    type: "Step Function",
    cron: "cron(0 5 ? * WED *)",
    status: "disabled",
    nextRun: "08 Jul, 06:00",
  },
  {
    id: "infra-sentiment-model-retrain",
    name: "sentiment_model_retrain-weekly",
    type: "Step Function",
    cron: "cron(0 2 ? * SAT *)",
    status: "enabled",
    nextRun: "11 Jul, 03:00",
  },
  {
    id: "infra-co-invest-syndicate-notify",
    name: "co_invest_syndicate_notify-batch",
    type: "EventBridge rule",
    cron: "cron(0 8 * * ? *)",
    status: "enabled",
    nextRun: "in 9h 08 min",
  },
  {
    id: "infra-audit-log-export",
    name: "audit_log_export-nightly",
    type: "Lambda function",
    cron: "cron(0 0 * * ? *)",
    status: "enabled",
    nextRun: "in 1h 41 min",
  },
  {
    id: "infra-secrets-rotation-check",
    name: "secrets_rotation_check-nightly",
    type: "EventBridge rule",
    cron: "cron(0 1 * * ? *)",
    status: "enabled",
    nextRun: "in 2h 46 min",
  },
  {
    id: "infra-dns-health-check",
    name: "dns_health_check-hourly",
    type: "EventBridge rule",
    cron: "cron(5 * * * ? *)",
    status: "enabled",
    nextRun: "in 27 min",
  },
  {
    id: "infra-backup-verification",
    name: "backup_verification-nightly",
    type: "ECS task",
    cron: "cron(30 4 * * ? *)",
    status: "enabled",
    nextRun: "in 5h 38 min",
  },
  {
    id: "infra-cost-explorer-export",
    name: "cost_explorer_export-daily",
    type: "Lambda function",
    cron: "cron(0 9 * * ? *)",
    status: "disabled",
    nextRun: "08 Jul, 10:00",
  },
  {
    id: "infra-search-index-optimize",
    name: "search_index_optimize-weekly",
    type: "ECS task",
    cron: "cron(0 3 ? * SAT *)",
    status: "enabled",
    nextRun: "11 Jul, 04:00",
  },
  {
    id: "infra-gp-led-secondary-pricing",
    name: "gp_led_secondary_pricing-ingest",
    type: "ECS task",
    cron: "cron(0 5 ? * TUE *)",
    status: "disabled",
    nextRun: "14 Jul, 06:00",
  },
  {
    id: "infra-co-investor-directory-sync",
    name: "co_investor_directory_sync-ingest",
    type: "Lambda function",
    cron: "cron(0 4 ? * THU *)",
    status: "enabled",
    nextRun: "09 Jul, 05:00",
  },
  {
    id: "infra-cap-table-reconciliation",
    name: "cap_table_reconciliation-nightly",
    type: "ECS task",
    cron: "cron(0 3 * * ? *)",
    status: "enabled",
    nextRun: "in 4h 08 min",
  },
];

/**
 * This fleet covers far more infra schedules than the hand-authored
 * "representative sample" above. `TOTAL_SCHEDULE_COUNT` is the real,
 * accurate total this page claims (shown in the header), and `getSchedule`
 * below extends the hardcoded `INFRA_SCHEDULES` array out to that count with
 * deterministic synthetic rows for infinite scroll, rather than pretending
 * the fleet stops where the hand-written sample happens to stop. Bounded
 * (unlike the runs history) because infra schedules are a curated set of
 * real AWS resources, not an ever-growing event log — this represents real
 * growth over time from the reference dashboard card's count of 25.
 */
const TOTAL_SCHEDULE_COUNT = 68;

/** Deterministic 32-bit string hash (FNV-1a) + seeded PRNG — same technique
 * as `runs.tsx`'s `generateSyntheticRun`, so the SAME index always produces
 * the SAME synthetic row (no `Math.random()`/`Date.now()`). */
function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DAY_OF_WEEK_POOL = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

// Fixed baseline for synthetic absolute "next run" dates — the day before
// the hardcoded set's earliest absolute date ("08 Jul, 10:00"), so synthetic
// weekly/batch rows land in the same plausible near-future window. Never
// derived from `new Date()`/`Date.now()`.
const NEXT_RUN_BASELINE = new Date(2026, 6, 7);

/** Distinct type values actually present in `INFRA_SCHEDULES` — the same
 * derivation technique `runs.tsx` uses for its `SECONDARY_POOL`
 * (`Array.from(new Set(...))`), reused here as the pool synthetic rows draw
 * from rather than inventing a separate list. (`status` isn't drawn from an
 * equivalent pool — see the weighted roll in `generateSyntheticSchedule`
 * below — since a flat 50/50 over `["enabled", "disabled"]` would skew far
 * more disabled than the hardcoded set's real ~3:1 ratio.) */
const SCHEDULE_TYPE_POOL: InfraScheduleType[] = Array.from(
  new Set(INFRA_SCHEDULES.map((schedule) => schedule.type)),
);

/** Every hardcoded `name` is `<subject>-<suffix>` (e.g.
 * `"companies_house_filings_sync-ingest"` splits into subject
 * `"companies_house_filings_sync"` and suffix `"ingest"`) — split on the
 * LAST hyphen since the subject itself only ever uses underscores. */
function splitScheduleName(name: string): { subject: string; suffix: string } {
  const separatorIndex = name.lastIndexOf("-");
  return {
    subject: name.slice(0, separatorIndex),
    suffix: name.slice(separatorIndex + 1),
  };
}

const SCHEDULE_NAME_PARTS = INFRA_SCHEDULES.map((schedule) =>
  splitScheduleName(schedule.name),
);
const SUBJECT_POOL: string[] = SCHEDULE_NAME_PARTS.map((parts) => parts.subject);
const SUFFIX_POOL: string[] = Array.from(
  new Set(SCHEDULE_NAME_PARTS.map((parts) => parts.suffix)),
);

/** A cadence family per suffix — governs both the cron shape and whether
 * "Next run" reads as a relative countdown (today) or an absolute date
 * (later this month), matching the mix already visible in the hardcoded
 * rows (`*-hourly`/`*-nightly`/`*-ingest`/`*-daily` read "in …", `*-weekly`/
 * `*-batch` read an absolute date). The hardcoded rows are NOT sorted by
 * "Next run" (row 3's absolute date is days out while row 4's "in 12 min" is
 * imminent), so synthetic rows don't need to preserve any ordering either —
 * unlike `runs.tsx`'s "Created" column, there's no existing sort to break. */
function cadenceFor(suffix: string): "hourly" | "daily" | "weekly" {
  if (suffix === "hourly") return "hourly";
  if (suffix === "weekly" || suffix === "batch") return "weekly";
  return "daily";
}

/** A schedule beyond the hand-authored `INFRA_SCHEDULES` fleet —
 * deterministic per `index`, so scrolling to the same position always shows
 * the same row. Subject/suffix are picked by cycling `index` through
 * `SUBJECT_POOL`/`SUFFIX_POOL` (rather than hashing) specifically so no
 * synthetic row ever reconstructs an existing hardcoded row's exact
 * `(subject, suffix)` pairing — with 30 subjects × 6 suffixes and only 38
 * synthetic rows needed, a hash-random pick risked both re-deriving a real
 * row's exact name and colliding with another synthetic row's name, which
 * would read as two identical resources in the table. */
function generateSyntheticSchedule(index: number): InfraSchedule {
  const rand = mulberry32(hashSeed(`infra-${index}`));

  const syntheticIndex = index - INFRA_SCHEDULES.length;
  const subjectIdx = syntheticIndex % SUBJECT_POOL.length;
  const suffixCycle = Math.floor(syntheticIndex / SUBJECT_POOL.length);
  const originalSuffixIdx = SUFFIX_POOL.indexOf(
    SCHEDULE_NAME_PARTS[subjectIdx].suffix,
  );
  const suffixIdx = (originalSuffixIdx + 1 + suffixCycle) % SUFFIX_POOL.length;
  const subject = SUBJECT_POOL[subjectIdx];
  const suffix = SUFFIX_POOL[suffixIdx];

  const type = SCHEDULE_TYPE_POOL[Math.floor(rand() * SCHEDULE_TYPE_POOL.length)];
  const status: ScheduleStatus = rand() < 0.75 ? "enabled" : "disabled";

  const minute = Math.floor(rand() * 60);
  const hour = Math.floor(rand() * 10);
  const cadence = cadenceFor(suffix);

  let cron: string;
  let nextRun: string;
  if (cadence === "hourly") {
    cron = `cron(${minute} * * * ? *)`;
    const minsAway = 5 + Math.floor(rand() * 55);
    nextRun = `in ${minsAway} min`;
  } else if (cadence === "daily") {
    cron = `cron(${minute} ${hour} * * ? *)`;
    const hoursAway = Math.floor(rand() * 10);
    const minsAway = Math.floor(rand() * 60);
    nextRun =
      hoursAway === 0
        ? `in ${minsAway} min`
        : `in ${hoursAway}h ${pad2(minsAway)} min`;
  } else {
    const dayOfWeek =
      DAY_OF_WEEK_POOL[Math.floor(rand() * DAY_OF_WEEK_POOL.length)];
    cron = `cron(${minute} ${hour} ? * ${dayOfWeek} *)`;
    const daysAway = 1 + Math.floor(rand() * 27);
    const runDate = new Date(
      NEXT_RUN_BASELINE.getFullYear(),
      NEXT_RUN_BASELINE.getMonth(),
      NEXT_RUN_BASELINE.getDate() + daysAway,
      hour,
      minute,
    );
    nextRun = `${pad2(runDate.getDate())} ${MONTH_ABBR[runDate.getMonth()]}, ${pad2(runDate.getHours())}:${pad2(runDate.getMinutes())}`;
  }

  return {
    id: `infra-${subject.replace(/_/g, "-")}-${suffix}`,
    name: `${subject}-${suffix}`,
    type,
    cron,
    status,
    nextRun,
  };
}

function getSchedule(index: number): InfraSchedule {
  return index < INFRA_SCHEDULES.length
    ? INFRA_SCHEDULES[index]
    : generateSyntheticSchedule(index);
}

type StatusFilter = "all" | ScheduleStatus;
type TypeFilter = "all" | InfraScheduleType;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All statuses" },
  { key: "enabled", label: "Enabled" },
  { key: "disabled", label: "Disabled" },
];

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "All types" },
  { key: "ECS task", label: "ECS task" },
  { key: "Lambda function", label: "Lambda function" },
  { key: "Step Function", label: "Step Function" },
  { key: "EventBridge rule", label: "EventBridge rule" },
];

/**
 * `/workflows/infra` "maximised" full-page table: every infra-level
 * schedule (ECS tasks, Lambda functions, Step Functions, EventBridge rules)
 * behind the reference dashboard's "Infra schedules" summary card, as a
 * proper full-width table instead of that card's compact scrollable list.
 * No page-level title here — the shell's breadcrumbs already read
 * "Workflows > Infra schedules" for this route. Status/type filtering lives
 * in the shell's secondary header, not inline in the page. All data is
 * static mock content — there is no live infra-scheduling backend in
 * `platform` yet.
 */
export function WorkflowInfraPage() {
  useDocumentTitle("Infra Schedules");

  const navigate = useNavigate();
  const goToDashboard = () => {
    if (!document.startViewTransition) {
      navigate("/workflows");
      return;
    }
    document.startViewTransition(() => flushSync(() => navigate("/workflows")));
  };

  const isInitialLoading = useSimulatedLoad();

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_SCHEDULE_COUNT,
    initialCount: INFRA_SCHEDULES.length,
    pageSize: 20,
    scrollRootRef: tableWrapRef,
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  // Filtering applies to whatever's been loaded so far, same as any
  // client-side filter over an infinite-scrolled list — it doesn't fetch
  // ahead to find matches beyond `loadedCount`.
  const loadedSchedules = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getSchedule(index)),
    [loadedCount],
  );

  const filteredSchedules = useMemo(
    () =>
      loadedSchedules.filter(
        (schedule) =>
          (statusFilter === "all" || schedule.status === statusFilter) &&
          (typeFilter === "all" || schedule.type === typeFilter),
      ),
    [loadedSchedules, statusFilter, typeFilter],
  );

  const isFiltered = statusFilter !== "all" || typeFilter !== "all";

  // The fleet is a small, bounded set (unlike the runs history), so this is
  // computed once over all `TOTAL_SCHEDULE_COUNT` rows rather than just the
  // loaded ones — the "N enabled" summary stays accurate at any scroll
  // position instead of drifting upward as more rows load in.
  const enabledCount = useMemo(
    () =>
      Array.from({ length: TOTAL_SCHEDULE_COUNT }, (_, index) =>
        getSchedule(index),
      ).filter((schedule) => schedule.status === "enabled").length,
    [],
  );

  const toolbar = useMemo(
    () => (
      <div className={styles.filterRow}>
        <div className={styles.tabBar} role="tablist" aria-label="Filter by status">
          {STATUS_FILTERS.map((option) => {
            const active = option.key === statusFilter;
            return (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={cn(styles.tab, active && styles.tabActive)}
                onClick={() => setStatusFilter(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <div className={styles.tabBar} role="tablist" aria-label="Filter by type">
          {TYPE_FILTERS.map((option) => {
            const active = option.key === typeFilter;
            return (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={cn(styles.tab, active && styles.tabActive)}
                onClick={() => setTypeFilter(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {/* Flex spacer, mirroring `../../qa-automation/header-and-stats.tsx`'s
            `.tabBarRow` — packs the filter tab bars hard left and reserves
            the right side of the row for a card-level action button. */}
        <span className={styles.spacer} />
        <button type="button" className={styles.startRunButton}>
          <Plus size={14} aria-hidden />
          New infra schedule
        </button>
      </div>
    ),
    [statusFilter, typeFilter],
  );

  useSecondaryHeader(toolbar);

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={{ viewTransitionName: "wf-card-infra" } as unknown as CSSProperties}
      >
        <header className={styles.header}>
          <span className={styles.summary}>
            {TOTAL_SCHEDULE_COUNT} schedules · {enabledCount} enabled
          </span>
          <span className={styles.count}>
            {isFiltered
              ? `${filteredSchedules.length} of ${loadedCount} loaded`
              : TOTAL_SCHEDULE_COUNT}
          </span>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
        <div ref={tableWrapRef} className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th} scope="col">
                  Name
                </th>
                <th className={styles.th} scope="col">
                  Type
                </th>
                <th className={styles.th} scope="col">
                  Cron
                </th>
                <th className={styles.th} scope="col">
                  Status
                </th>
                <th className={`${styles.th} ${styles.thRight}`} scope="col">
                  Next run
                </th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoading ? (
                Array.from({ length: 8 }, (_, index) => (
                  <SkeletonRow key={`initial-skeleton-${index}`} columns={5} />
                ))
              ) : (
                <>
                  {filteredSchedules.map((schedule) => (
                    <tr key={schedule.id} className={styles.row}>
                      <td className={`${styles.td} ${styles.nameCell}`} title={schedule.name}>
                        {schedule.name}
                      </td>
                      <td className={styles.td}>
                        <span className={styles.typeTag}>{schedule.type}</span>
                      </td>
                      <td className={`${styles.td} ${styles.cronCell}`}>{schedule.cron}</td>
                      <td className={styles.td}>
                        <StatusPill tone={schedule.status === "enabled" ? "success" : "neutral"}>
                          {schedule.status === "enabled" ? "Enabled" : "Disabled"}
                        </StatusPill>
                      </td>
                      <td className={`${styles.td} ${styles.nextRunCell}`}>{schedule.nextRun}</td>
                    </tr>
                  ))}
                  {filteredSchedules.length === 0 && !hasMore && (
                    <tr>
                      <td className={styles.emptyCell} colSpan={5}>
                        No infra schedules match the current filters.
                      </td>
                    </tr>
                  )}
                  {hasMore && (
                    <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={5} />
                  )}
                  {isLoadingMore &&
                    Array.from({ length: 3 }, (_, index) => (
                      <SkeletonRow key={`skeleton-${index}`} columns={5} />
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </section>
    </div>
  );
}
